import { useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";

import { useSmokeballBillingFeatureFlagsContext } from "@smart/bridge-feature-flags-dom";
import { Country } from "@smart/bridge-types-basic";
import { loadLocale } from "@smart/itops-locale-dom";
import {
  Alert,
  CurrencyInput,
  Select,
} from "@smart/itops-sb-design-system-dom";
import { useLoadBankAccounts } from "@smart/manage-gql-client-dom";

import { baseLanguageKey, LanguageKey } from "../../../../types";
import { GqlFieldValues, OnUpdateField } from "../../types";

type PaymentProps = {
  onUpdateField: OnUpdateField;
  field: GqlFieldValues;
  viewingLanguage: LanguageKey;
};

export const Payment = ({
  field: fieldValues,
  onUpdateField,
  viewingLanguage,
}: PaymentProps) => {
  const isViewingBaseLanguage = viewingLanguage === baseLanguageKey;
  const bankAccounts = useLoadBankAccounts({});
  const { amountInCents, bankAccount } = fieldValues.payment || {};
  const {
    featureFlags: { bb12038: canAddSBPayments },
  } = useSmokeballBillingFeatureFlagsContext();
  const locale = loadLocale();
  const formMethods = useForm<{
    amountInCents?: number | null;
    bankAccount?: {
      id: string;
      type: number;
    } | null;
  }>({
    defaultValues: {
      amountInCents,
      bankAccount,
    },
  });

  const { control, handleSubmit, getValues, setValue } = formMethods;

  const submit = handleSubmit(async (values) => {
    await onUpdateField({
      field: fieldValues,
      updated: {
        payment: {
          amountInCents: values.amountInCents,
          bankAccount: values.bankAccount,
        },
      },
    });
  });

  useEffect(() => {
    if (bankAccounts.result?.length && !getValues("bankAccount.id")) {
      setValue("bankAccount", bankAccounts.result[0].identifier);
      submit().catch(console.error);
    }
  }, [bankAccounts.result]);

  const disabled = !bankAccounts.loading && !bankAccounts.result?.length;
  const getMessage = () => {
    if (bankAccounts.loading) return "loading...";
    return disabled
      ? "No accounts linked to Smokeball Payments"
      : "Can only be used with Smokeball Payments";
  };

  const getAlertProps = () => {
    type AlertLinks = "settings" | "support" | "contact";

    const links: Record<Country, Record<AlertLinks, string>> = {
      AU: {
        settings: "https://app.smokeball.com.au/#/settings/payment-provider",
        support:
          "https://support.smokeball.com/hc/en-au/articles/18768295875607",
        contact: "https://support.smokeball.com/hc/en-au/requests/new",
      },
      GB: {
        settings: "https://app.smokeball.co.uk/#/settings/payment-provider",
        support:
          "https://support.smokeball.com/hc/en-gb/articles/18768295875607",
        contact: "https://support.smokeball.com/hc/en-gb/requests/new",
      },
      US: {
        settings: "https://app.smokeball.com/#/settings/payment-provider",
        support:
          "https://support.smokeball.com/hc/en-us/articles/18768295875607",
        contact: "https://support.smokeball.com/hc/en-us/requests/new",
      },
    };

    const createLink = (type: AlertLinks, text: string) => ({
      text,
      onClick: () =>
        window.open(links[locale.country][type], "noopener,noreferrer"),
    });

    const content =
      "Accept card payments via this form with Smokeball Payments";

    if (!canAddSBPayments)
      return {
        content,
        actions: [
          createLink("support", "Learn More"),
          {
            ...createLink("contact", "Contact Us"),
            variant: "subtle" as const,
          },
        ],
      };
    if (disabled)
      return {
        content,
        actions: [
          createLink("settings", "Apply"),
          {
            ...createLink("support", "Learn More"),
            variant: "subtle" as const,
          },
        ],
      };
    return {
      content: "Can only be used with Smokeball Payments",
    };
  };

  return (
    <div className="mx-[0.4rem]">
      <FormProvider {...formMethods}>
        {(!canAddSBPayments || disabled) && (
          <div className="mb-[1.6rem]">
            <Alert {...getAlertProps()} variant="section" palette="warning" />
          </div>
        )}
        <div className="flex flex-col gap-small">
          <Controller
            control={control}
            name="amountInCents"
            render={({ field }) => (
              <CurrencyInput
                label="Amount"
                intlConfig={locale}
                defaultValue={field.value ? field.value / 100 : undefined}
                onValueChange={(_, __, values) => {
                  if (values?.float) field.onChange(values.float * 100);
                }}
                onBlur={submit}
                disabled={disabled || !isViewingBaseLanguage}
              />
            )}
          />
          <Controller
            control={control}
            name="bankAccount"
            render={({ field, fieldState }) => (
              <Select
                id={field.name}
                label="Linked account"
                placeholder="-"
                options={(bankAccounts.result || [])
                  .filter((a) => !!a.identifier)
                  .map((account) => ({
                    label:
                      account.displayName ||
                      account.name ||
                      `${account.typeDisplayName} - Account`,
                    value: account.identifier,
                  }))}
                valueComparer={(currentValue, valueToCompare) =>
                  currentValue?.id === valueToCompare?.id
                }
                error={!!fieldState.error}
                message={fieldState.error?.message || getMessage()}
                value={
                  // Fall back to null so the input doesn't change between controlled and uncontrolled
                  (bankAccounts.result?.find(
                    (account) => account.identifier?.id === field.value?.id,
                  )?.identifier || null) as NonNullable<
                    ReturnType<typeof useLoadBankAccounts>["result"]
                  >[number]["identifier"]
                }
                onChange={async (identifier) => {
                  field.onChange(identifier);
                  await submit();
                }}
                disabled={disabled || !isViewingBaseLanguage}
              />
            )}
          />
        </div>
      </FormProvider>
    </div>
  );
};
